/**
 * Admin Dashboard Hooks
 * React hooks for fetching admin monitoring data
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  OverviewStats,
  TenderLinksResponse,
  TenderLinkDetail,
  ParcelsResponse,
  AdminParcelDetail,
  SourcesResponse,
  AdminFilters,
} from '@tender-monitor/shared/types/admin';
import {
  fetchOverview,
  fetchTenderLinks,
  fetchTenderLinkDetail,
  fetchAdminParcels,
  fetchAdminParcelDetail,
  fetchAdminSources,
  toggleSourceStatus,
} from '../services/adminApi';

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

// Overview Stats Hook
export function useOverviewStats() {
  const [data, setData] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchOverview();
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview');
      setLoading('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load };
}

// Tender Links Hook
export function useTenderLinks(filters: AdminFilters = {}) {
  const [data, setData] = useState<TenderLinksResponse | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchTenderLinks(filters);
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tender links');
      setLoading('error');
    }
  }, [filters.status, filters.startDate, filters.endDate, filters.page, filters.limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load };
}

// Tender Link Detail Hook
export function useTenderLinkDetail(id: string | null) {
  const [data, setData] = useState<TenderLinkDetail | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchTenderLinkDetail(id);
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tender link details');
      setLoading('error');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load };
}

// Parcels Hook
export function useAdminParcels(filters: AdminFilters = {}) {
  const [data, setData] = useState<ParcelsResponse | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchAdminParcels(filters);
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load parcels');
      setLoading('error');
    }
  }, [filters.status, filters.startDate, filters.endDate, filters.page, filters.limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load };
}

// Parcel Detail Hook
export function useParcelDetail(id: string | null) {
  const [data, setData] = useState<AdminParcelDetail | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchAdminParcelDetail(id);
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load parcel details');
      setLoading('error');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load };
}

// Sources Hook
export function useSources() {
  const [data, setData] = useState<SourcesResponse | null>(null);
  const [loading, setLoading] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading('loading');
    setError(null);
    try {
      const result = await fetchAdminSources();
      setData(result);
      setLoading('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sources');
      setLoading('error');
    }
  }, []);

  const toggle = useCallback(async (sourceId: string, isActive: boolean) => {
    setTogglingIds(prev => new Set(prev).add(sourceId));
    try {
      await toggleSourceStatus(sourceId, isActive);
      // Update local state
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.id === sourceId
              ? { ...item, isActive, errorCount: isActive ? 0 : item.errorCount }
              : item
          ),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle source');
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading: loading === 'loading', error, reload: load, toggle, togglingIds };
}
